import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Store, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function BranchRegister() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | invalid | form | saving
  const [branchName, setBranchName] = useState("");
  const [f, setF] = useState({ owner_name: "", email: "", password: "", address: "", phone: "", delivery_radius_miles: 2, shop_postcode: "" });

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/branch/invite/${token}`).then(({ data }) => {
      if (!data.valid) { setState("invalid"); return; }
      setBranchName(data.name);
      setF((x) => ({ ...x, email: data.owner_email || "" }));
      setState("form");
    }).catch(() => setState("invalid"));
  }, [token]);

  const submit = async () => {
    if (!f.owner_name || !f.email || f.password.length < 6 || !f.address || !f.shop_postcode) {
      toast.error("Please fill all fields (password min 6 chars)"); return;
    }
    setState("saving");
    try {
      await api.post("/branch/register", { token, ...f, delivery_radius_miles: Number(f.delivery_radius_miles) });
      toast.success("Branch set up! Please sign in to your new admin.");
      navigate("/login");
    } catch (e) {
      toast.error(apiError(e));
      setState("form");
    }
  };

  if (state === "loading") return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-metros-red" size={32} /></div>;
  if (state === "invalid") return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="branch-register-invalid">
      <h1 className="font-heading font-extrabold text-2xl">Invalid or expired invite</h1>
      <p className="text-muted-foreground mt-2">This branch invite link is not valid or has already been used.</p>
    </div>
  );

  const Field = ({ label, k, type = "text", placeholder }) => (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">{label}</label>
      <input data-testid={`br-${k}`} type={type} value={f[k]} placeholder={placeholder}
        onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className="w-full mt-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="branch-register-form">
      <div className="bg-metros-red/10 text-metros-red rounded-2xl p-3 w-fit mx-auto mb-2"><Store size={26} /></div>
      <h1 className="font-heading font-extrabold text-3xl text-center">Set up {branchName}</h1>
      <p className="text-sm text-muted-foreground text-center mb-6">Complete your branch details to activate your admin account.</p>
      <div className="space-y-3">
        <Field label="Your name" k="owner_name" placeholder="Owner name" />
        <Field label="Login email" k="email" type="email" placeholder="you@example.com" />
        <Field label="Password" k="password" type="password" placeholder="Min 6 characters" />
        <Field label="Restaurant address" k="address" placeholder="Full address" />
        <Field label="Phone" k="phone" placeholder="Contact number" />
        <div className="flex gap-3">
          <div className="flex-1"><Field label="Shop postcode" k="shop_postcode" placeholder="e.g. KT1 1AA" /></div>
          <div className="w-32"><Field label="Delivery radius (mi)" k="delivery_radius_miles" type="number" /></div>
        </div>
        <button data-testid="br-submit" onClick={submit} disabled={state === "saving"}
          className="w-full bg-metros-red text-white rounded-full py-3.5 font-bold flex justify-center items-center gap-2 disabled:opacity-60">
          {state === "saving" ? <Loader2 className="animate-spin" size={18} /> : "Complete Registration"}
        </button>
      </div>
    </div>
  );
}
